import { Fragment, useState, useEffect, ReactNode } from 'react';
import debounce from 'lodash/debounce';
import styles from './infinite-scroll-comp.module.scss';

import Spinner from '../spinner/spinner';

type Props = {
  children: ReactNode;
  className?: string;
  getScrollParent?: HTMLDivElement | null | undefined;
  hasMore: boolean | null | undefined;
  loader?: ReactNode;
  onInfiniteScroll: () => any;
  onInit?: () => any;
  style?: any;
  threshold?: number;
  untilScrollBarAppears?: boolean;
  useLoader?: boolean;
  useWindow?: boolean;
};

export const InfiniteScroll = ({
  children,
  className,
  getScrollParent,
  hasMore,
  loader,
  onInfiniteScroll,
  onInit,
  style,
  threshold = 250,
  untilScrollBarAppears,
  useLoader = true,
  useWindow = true,
}: Props) => {
  const [shouldCheckLoadMore, setShouldCheckLoadMore] = useState<boolean>(true);
  const [isBusy, setIsBusy] = useState<boolean>(true);

  const scrollParent = getScrollParent;

  const maxRecursions = 5;

  useEffect(() => {
    loadInit();
  }, []);

  useEffect(() => {
    if (!hasMore) {
      setIsBusy(false);
    }
  }, [hasMore]);

  useEffect(() => {
    const debounceHandleScroll = debounce(() => {
      handleScroll();
    }, 500);

    if (useWindow === false && !!scrollParent) {
      scrollParent.addEventListener('scroll', debounceHandleScroll);
      scrollParent.addEventListener('resize', checkScroll);
    } else {
      window.addEventListener('scroll', debounceHandleScroll);
      window.addEventListener('resize', checkScroll);
    }

    return () => {
      if (useWindow === false && !!scrollParent) {
        scrollParent.removeEventListener('scroll', debounceHandleScroll);
        scrollParent.removeEventListener('resize', checkScroll);
      } else {
        window.removeEventListener('scroll', debounceHandleScroll);
        window.removeEventListener('resize', checkScroll);
      }
    };
  }, [scrollParent, onInfiniteScroll, hasMore, useWindow, threshold, isBusy]);

  // run the first time we set isBusy to false
  useEffect(() => {
    if (shouldCheckLoadMore && !isBusy) {
      setShouldCheckLoadMore(false);
      checkScroll();
    }
  }, [isBusy, shouldCheckLoadMore]);

  const handleScroll = () => {
    if (!hasMore || isBusy) {
      return;
    }

    /* With the whole page being the container its easier to know when to load and not
     * because you can just use the YOffset and subtract the threshold from the body instead */
    const noContainerButScrolled =
      !scrollParent &&
      useWindow &&
      Math.ceil(window.pageYOffset + window.innerHeight) >=
        document.body.scrollHeight - threshold;

    /* because we're inside of a container we have to constantly add and keep track of scrollTop + threshold
     * in order to properly know when to attempt to load new content */
    const containerAndScrolled =
      scrollParent &&
      !useWindow &&
      scrollParent.clientHeight + scrollParent.scrollTop + threshold >=
        scrollParent.scrollHeight;

    /* in either scenario we want to infiniteScroll, we just need to make sure 1 is correct
     * and that we actually have more content */
    if (noContainerButScrolled || containerAndScrolled) {
      onUserScroll().then(() => {
        setIsBusy(false);
      });
    }
  };

  const checkScroll = () => {
    if (
      hasMore &&
      !isBusy &&
      !hasVerticalScrollBar() &&
      untilScrollBarAppears
    ) {
      loadUntilScrollBarAppears();
    }
  };

  const loadInit = () => {
    if (onInit) {
      onInit().then(() => {
        setIsBusy(false);
        if (untilScrollBarAppears) {
          loadUntilScrollBarAppears();
        }
      });
    } else {
      setIsBusy(false);
    }
  };

  const hasVerticalScrollBar = () => {
    if (scrollParent && !useWindow) {
      return scrollParent.scrollHeight > scrollParent.clientHeight;
    }

    // if the browser is zoomed by a non-integer factor, e.g. 80%
    // rounding errors can cause a 1 pixel difference and a false positive for the scrollbar
    return document.body.scrollHeight > window.innerHeight + 1;
  };

  const loadUntilScrollBarAppears = (attempts = maxRecursions) => {
    if (isBusy) return;

    if (!hasVerticalScrollBar()) {
      onUserScroll().then(() => {
        // wait for newly loaded entries to be rendered before checking for scrollbar again
        // otherwise the same data can be loaded several times
        setTimeout(() => {
          if (hasMore && attempts > 0) {
            loadUntilScrollBarAppears(--attempts);
            setIsBusy(false);
          }
        }, 50);
      });
    }
  };

  const onUserScroll = () => {
    setIsBusy(true);
    return onInfiniteScroll();
  };

  const Element = className || style ? 'div' : Fragment;
  const divProps = {
    ...(className ? { className: className } : {}),
    ...(style ? { position: 'relative', ...style } : {}),
  } as const;

  return (
    <Element {...divProps}>
      {children}
      {isBusy && useLoader && (
        <div className={styles['spinner-container']}>
          {loader ? loader : <Spinner top='0' />}
        </div>
      )}
    </Element>
  );
};

InfiniteScroll.displayName = 'InfiniteScroll';
export default InfiniteScroll;
